<template>
  <v-dialog :value="value" @input="$emit('input', $event)" persistent max-width="700">
    <v-card>
      <v-card-title class="primary white--text">
        {{ $t('dueDiligence.shareReport') }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon style="color: white !important;">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-4">
        <v-radio-group v-model="shareReportType" hide-details class="mt-0 mb-4">
          <v-radio
            :label="$t('dueDiligence.shareReportWithInternalUser')"
            value="internal"
            color="primary"
          >
            <template v-slot:label>
              <span>{{ $t('dueDiligence.shareReportWithInternalUser') }}</span>
              <v-tooltip bottom max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small class="ml-1" color="primary" v-bind="attrs" v-on="on">
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>{{ $t('dueDiligence.shareReportWithInternalUserTooltip') }}</span>
              </v-tooltip>
            </template>
          </v-radio>
          <v-radio
            :label="$t('dueDiligence.shareReportWithExternalUser')"
            value="external"
            color="primary"
          >
            <template v-slot:label>
              <span>{{ $t('dueDiligence.shareReportWithExternalUser') }}</span>
              <v-tooltip bottom max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small class="ml-1" color="primary" v-bind="attrs" v-on="on">
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>{{ $t('dueDiligence.shareReportWithExternalUserTooltip') }}</span>
              </v-tooltip>
            </template>
          </v-radio>
        </v-radio-group>

        <label class="mb-2 d-block">{{ $t('dueDiligence.enterUserEmail') }}</label>
        <div class="d-flex align-center mb-4">
          <v-text-field
            v-model="shareReportEmail"
            :placeholder="$t('dueDiligence.enterUserEmail')"
            outlined
            dense
            hide-details
            clearable
            class="mr-2"
            @keyup.enter="searchUserByEmail"
          ></v-text-field>
          <v-btn
            color="primary"
            :loading="searchingUser"
            :disabled="!shareReportEmail?.trim()"
            @click="searchUserByEmail"
          >
            {{ $t('search') }}
          </v-btn>
        </div>

        <!-- Access Level Section (only for external users) -->
        <div v-if="shareReportType === 'external'" class="mt-4 mb-4">
          <p class="font-weight-medium mb-2">{{ $t('dueDiligence.accessLevel') }}</p>
          <v-radio-group v-model="shareAccessLevel" hide-details class="mt-0 relative">
            <v-radio
              :label="$t('dueDiligence.shareReadOnlyReport')"
              value="read-only"
              color="primary"
            >
              <template v-slot:label>
                <span class="font-weight-medium">{{ $t('dueDiligence.shareReadOnlyReport') }}</span>
              </template>
            </v-radio>
            <v-radio
              :label="$t('dueDiligence.shareFullDataReport')"
              value="full-data"
              color="primary"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-medium">{{ $t('dueDiligence.shareFullDataReport') }}</div>
                </div>
              </template>
            </v-radio>
            <div v-if="shareAccessLevel === 'full-data'" class="text-caption grey--text" style="font-weight: 400; margin-left: 30px; margin-top: -8px;">
                    {{ $t('dueDiligence.shareFullDataReportDescription') }}
            </div>
          </v-radio-group>
        </div>

        <!-- User Information Display -->
        <div v-if="sharedUserDetails" class="mt-4 pa-3" style="background-color: #f5f5f5; border-radius: 4px;">
          <v-row class="text-body-2">
            <v-col sm="6">
              <p class="mb-1">{{ $t('deforestation.name') }}</p>
              <strong>{{ sharedUserDetails.name || '-' }}</strong>
            </v-col>
            <v-col sm="6">
              <p class="mb-1">{{ $t('deforestation.email') }}</p>
              <strong>{{ sharedUserDetails.email || '-' }}</strong>
            </v-col>
            <v-col sm="6">
              <p class="mb-1">{{ $t('deforestation.phoneNumber') }}</p>
              <strong>{{ sharedUserDetails.phoneNumber || '-' }}</strong>
            </v-col>
            <v-col sm="6">
              <p class="mb-1">{{ $t('deforestation.country') }}</p>
              <strong>{{ sharedUserDetails.country || '-' }}</strong>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined @click="close">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="confirmLoading"
          :disabled="!canConfirmShare"
          @click="confirmShareReport"
        >
          {{ $t('confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DeforestationService from '@/_services/DeforestationService';

export default {
  name: 'ShareReportDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    diligenceReportId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      shareReportType: 'internal',
      shareReportEmail: '',
      shareAccessLevel: 'full-data',
      sharedUserDetails: null,
      searchingUser: false,
      confirmLoading: false,
    };
  },
  computed: {
    canConfirmShare() {
      return !!this.shareReportEmail?.trim();
    },
  },
  watch: {
    value(visible) {
      if (visible) {
        this.shareReportType = 'internal';
        this.shareReportEmail = '';
        this.shareAccessLevel = 'full-data';
        this.sharedUserDetails = null;
        this.searchingUser = false;
      }
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
    },
    async searchUserByEmail() {
      if (!this.shareReportEmail?.trim()) return;
      this.searchingUser = true;
      try {
        if (this.shareReportType === 'internal') {
          const res = await DeforestationService.searchDdsUserByEmail(this.shareReportEmail.trim());
          const user = res?.data?.findUserByEmail;
          if (user) {
            this.sharedUserDetails = {
              id: user.id,
              name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || '-',
              email: user.email,
              phoneNumber: user.mobile || '-',
              country: user.countryId || '-',
            };
          } else {
            this.sharedUserDetails = null;
            this.$notify({
              text: this.$t('dueDiligence.userSearchError') || 'User not found.',
              type: 'warning',
            });
          }
        } else {
          this.sharedUserDetails = {
            email: this.shareReportEmail.trim(),
            name: '-',
            phoneNumber: '-',
            country: '-',
          };
        }
      } catch (error) {
        console.error('Error searching user:', error);
        this.$notify({
          text: this.$t('dueDiligence.userSearchError') || 'Failed to search user.',
          type: 'error',
        });
        this.sharedUserDetails = null;
      } finally {
        this.searchingUser = false;
      }
    },
    async confirmShareReport() {
      if (!this.shareReportEmail?.trim()) {
        this.$notify({
          text: this.$t('dueDiligence.pleaseEnterEmail') || 'Please enter an email address.',
          type: 'warning',
        });
        return;
      }
      try {
        this.confirmLoading = true;
        const shareInput = {
          diligenceReportId: this.diligenceReportId,
          email: this.shareReportEmail.trim(),
          shareType: this.shareReportType,
          accessLevel: this.shareAccessLevel || null,
        };
        await DeforestationService.shareDdsReport(shareInput);
        this.$notify({
          text: this.$t('dueDiligence.reportSharedSuccessfully') || 'Report shared successfully.',
          type: 'success',
        });
        this.close();
        this.$emit('shared');
      } catch (error) {
        console.error('Error sharing report:', error);
        this.$notify({
          text: this.$t('dueDiligence.shareReportError') || 'Failed to share report.',
          type: 'error',
        });
      } finally {
        this.confirmLoading = false;
      }
    },
  },
};
</script>
